(() => {
  'use strict';

  const RUNTIME_KEY = '__ecResinPdpV3R51';
  const ROOT_SELECTOR = '.ec-sot-v3-section[data-ec-v3], [data-ec-section^="EC-SEC-"]';

  if (window[RUNTIME_KEY]?.boot) {
    window[RUNTIME_KEY].boot(document);
    return;
  }

  const states = new WeakMap();
  const activeRoots = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const dialogReturnFocus = new WeakMap();
  const openDialogs = new Set();
  let pageOverflowBeforeDialog = '';

  const CONTRACT_EVENT_MAP = {
    section_view: 'ec_section_viewed',
    study_opened: 'ec_proof_opened',
    study_source_opened: 'ec_proof_opened',
    report_opened: 'ec_coa_open',
    report_source_opened: 'ec_coa_open',
    proof_selected: 'ec_review_theme_selected',
    routine_step_selected: 'ec_motion_step_view',
    faq_open: 'ec_faq_open',
    offer_progression: 'ec_final_cta_click'
  };

  function token(value, fallback = '') {
    if (typeof value !== 'string') return fallback;
    return value.slice(0, 120).replace(/[^a-zA-Z0-9_.:/-]/g, '_') || fallback;
  }

  function sectionIdFor(section) {
    const declared = section.dataset.ecSection || section.dataset.ecSectionId || section.dataset.sectionId;
    if (declared) return token(declared.toUpperCase());
    const match = section.id?.match(/^ec-sec-(\d+)/i);
    return match ? `EC-SEC-${match[1].padStart(2, '0')}` : 'EC-SEC-UNKNOWN';
  }

  function routeIdFor(section) {
    return token(
      section.dataset.ecRouteId
        || section.dataset.routeId
        || document.body?.dataset.ecRouteId
        || document.body?.dataset.routeId
        || 'SH-P01-RES',
      'SH-P01-RES'
    );
  }

  function analyticsPayload(section, extra = {}) {
    const payload = {
      section_id: sectionIdFor(section),
      route_id: routeIdFor(section)
    };

    Object.entries(extra).forEach(([key, value]) => {
      if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
        payload[key] = value;
      } else if (typeof value === 'string' && value) {
        payload[key] = token(value);
      }
    });

    return payload;
  }

  function contractPayload(payload) {
    const governed = {
      section_id: payload.section_id,
      route: payload.route_id,
      source: payload.source || 'resin_pdp_v3_r51'
    };

    ['faq_id', 'step_index', 'media_slot', 'status'].forEach((key) => {
      if (payload[key] !== undefined) governed[key] = payload[key];
    });
    return governed;
  }

  function emit(section, name, extra = {}) {
    const payload = analyticsPayload(section, extra);
    let handled = false;

    const governedName = CONTRACT_EVENT_MAP[name];
    if (governedName && typeof window.ecTrack === 'function') {
      try {
        handled = window.ecTrack(governedName, contractPayload(payload)) === true;
      } catch (_error) {
        handled = false;
      }
    }

    if (!handled && window.Shopify?.analytics?.publish) {
      try {
        window.Shopify.analytics.publish(`essencraft:${name}`, payload);
        handled = true;
      } catch (_error) {
        handled = false;
      }
    }

    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload, bubbles: true }));
    } catch (_error) {
      /* Analytics must never interrupt the storefront. */
    }

    if (!handled) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
  }

  function listen(state, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    state.cleanups.push(() => target.removeEventListener(type, handler, options));
  }

  function observe(state, observer, element) {
    observer.observe(element);
    state.observers.push(observer);
  }

  function lockPage(dialog) {
    if (!openDialogs.size) {
      pageOverflowBeforeDialog = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    openDialogs.add(dialog);
  }

  function unlockPage(dialog) {
    openDialogs.delete(dialog);
    if (!openDialogs.size) {
      document.documentElement.style.overflow = pageOverflowBeforeDialog;
    }
  }

  function dialogEventFor(section) {
    return sectionIdFor(section) === 'EC-SEC-08' ? 'report_opened' : 'study_opened';
  }

  function initReveal(section, state) {
    const targets = [section, ...section.querySelectorAll('[data-reveal]')];
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .16, rootMargin: '0px 0px -7% 0px' });

    targets.forEach((target) => observer.observe(target));
    state.observers.push(observer);
  }

  function initSectionView(section, state) {
    if (section.dataset.ecR51SectionViewed === 'true') return;

    const recordView = () => {
      if (section.dataset.ecR51SectionViewed === 'true') return;
      section.dataset.ecR51SectionViewed = 'true';
      emit(section, 'section_view');
      if (sectionIdFor(section) === 'EC-SEC-16') emit(section, 'offer_view');
    };

    if (!('IntersectionObserver' in window)) {
      recordView();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      recordView();
    }, { threshold: .25, rootMargin: '0px 0px -8% 0px' });
    observe(state, observer, section);
  }

  function initDialogs(section, state) {
    section.querySelectorAll('[data-ec-v3-dialog-open]').forEach((button) => {
      const open = () => {
        const id = button.getAttribute('aria-controls');
        const dialog = id ? document.getElementById(id) : null;
        if (!dialog) return;

        dialogReturnFocus.set(dialog, button);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        lockPage(dialog);
        dialog.querySelector('[data-ec-v3-dialog-close]')?.focus();

        emit(section, button.dataset.ecEvent || dialogEventFor(section), {
          source: button.dataset.ecEventSource || button.id || 'dialog_trigger'
        });
      };
      listen(state, button, 'click', open);
    });

    section.querySelectorAll('dialog[data-ec-v3-dialog]').forEach((dialog) => {
      const close = () => {
        if (dialog.open && typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        unlockPage(dialog);
        dialogReturnFocus.get(dialog)?.focus();
      };

      dialog.querySelectorAll('[data-ec-v3-dialog-close]').forEach((button) => {
        listen(state, button, 'click', close);
      });

      listen(state, dialog, 'cancel', (event) => {
        event.preventDefault();
        close();
      });
      listen(state, dialog, 'click', (event) => {
        if (event.target === dialog) close();
      });
      listen(state, dialog, 'close', () => unlockPage(dialog));

      dialog.querySelectorAll('.ec-modal__source').forEach((source) => {
        listen(state, source, 'click', () => {
          const openedEvent = dialogEventFor(section);
          emit(section, openedEvent === 'report_opened' ? 'report_source_opened' : 'study_source_opened', {
            source: source.id || 'modal_source'
          });
        });
      });

      state.cleanups.push(() => {
        if (!dialog.open) return;
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        unlockPage(dialog);
      });
    });
  }

  function initProof(section, state) {
    const quote = section.querySelector('[data-proof-current]');
    const label = section.querySelector('[data-proof-label]');
    const link = section.querySelector('[data-proof-link]');
    const selector = section.querySelector('.ec-proof-selector');
    const options = [...section.querySelectorAll('[data-proof-option]')];

    options.forEach((option, index) => {
      listen(state, option, 'click', () => {
        options.forEach((item) => {
          const selected = item === option;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-selected', String(selected));
        });
        if (quote) {
          quote.textContent = option.dataset.proofQuote || '';
          quote.classList.remove('is-switching');
          requestAnimationFrame(() => quote.classList.add('is-switching'));
        }
        if (label) label.textContent = option.dataset.proofLabel || '';
        if (link) link.href = option.dataset.proofSource || '#';
        selector?.style.setProperty('--proof-index', option.dataset.proofIndex || String(index));
        emit(section, 'proof_selected', {
          step_index: Number(option.dataset.proofIndex || index),
          source: `proof_${index + 1}`
        });
      });
    });
  }

  function initRoutine(section, state) {
    const routine = section.querySelector('.ec-routine');
    const steps = [...section.querySelectorAll('[data-routine-step]')];
    const tip = section.querySelector('[data-routine-current-tip]');
    if (!routine || !steps.length) return;

    const interval = Math.max(100, Number(routine.dataset.routineInterval || 900));
    let timers = [];
    const clearTimers = () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers = [];
      routine.removeAttribute('data-routine-playing');
    };

    const activate = (step, shouldTrack = false) => {
      const index = Number(step.dataset.routineIndex || 0);
      steps.forEach((item) => {
        const selected = item === step;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-selected', String(selected));
      });
      routine.style.setProperty('--routine-progress', `${index * 50}%`);
      if (tip) {
        tip.textContent = step.dataset.routineTip || '';
        tip.classList.remove('is-switching');
        requestAnimationFrame(() => tip.classList.add('is-switching'));
      }
      if (shouldTrack) emit(section, 'routine_step_selected', { step_index: index, source: 'manual' });
    };

    steps.forEach((step) => {
      listen(state, step, 'click', () => {
        clearTimers();
        activate(step, true);
      });
    });

    if (!reducedMotion.matches && 'IntersectionObserver' in window && routine.dataset.routinePlayed !== 'true') {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        routine.dataset.routinePlayed = 'true';
        routine.dataset.routinePlaying = 'true';
        timers = steps.map((step, index) => window.setTimeout(() => {
          activate(step);
          if (index === steps.length - 1) routine.removeAttribute('data-routine-playing');
        }, index * interval));
      }, { threshold: .48 });
      observe(state, observer, routine);
    }

    state.cleanups.push(clearTimers);
  }

  function initSensory(section, state) {
    const choices = [...section.querySelectorAll('[data-sensory-choice]')];
    const label = section.querySelector('[data-sensory-current-label]');
    const tip = section.querySelector('[data-sensory-current]');
    choices.forEach((choice) => {
      listen(state, choice, 'click', () => {
        choices.forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-selected', String(selected));
        });
        if (label) label.textContent = choice.dataset.sensoryLabel || '';
        if (tip) {
          tip.textContent = choice.dataset.sensoryTip || '';
          tip.classList.remove('is-switching');
          requestAnimationFrame(() => tip.classList.add('is-switching'));
        }
      });
    });
  }

  function initCounts(section, state) {
    section.querySelectorAll('[data-count]').forEach((element) => {
      if (element.dataset.counted === 'true') return;
      const target = Number(element.dataset.count || 0);
      const decimals = Number(element.dataset.decimals || 0);
      const duration = Math.max(1, Number(element.dataset.countDuration || 1050));
      const finish = () => {
        element.dataset.counted = 'true';
        element.textContent = target.toFixed(decimals);
      };

      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        finish();
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        element.dataset.counted = 'true';
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.max(0, Math.min(1, (now - start) / duration));
          element.textContent = (target * (1 - Math.pow(1 - progress, 3))).toFixed(decimals);
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: .65 });
      observe(state, observer, element);
    });
  }

  function initOutcomes(section, state) {
    if (!section.matches('.ec-sec-04') && sectionIdFor(section) !== 'EC-SEC-04') return;
    const panels = [...section.querySelectorAll('[data-outcome-panel]')];
    const progress = [...section.querySelectorAll('.ec-outcome-progress span')];
    const pinnedScene = section.querySelector('.ec-sec-04__inner');
    if (!panels.length || !pinnedScene) return;

    let active = -1;
    let frame = 0;
    const setOutcome = (index) => {
      if (index === active) return;
      active = index;
      panels.forEach((panel, panelIndex) => {
        const selected = panelIndex === index;
        panel.classList.toggle('is-active', selected);
        panel.setAttribute('aria-hidden', String(!selected));
      });
      progress.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
    };
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      if (rect.bottom <= 0) {
        setOutcome(panels.length - 1);
        return;
      }
      if (rect.top >= window.innerHeight) {
        setOutcome(0);
        return;
      }
      const scrollable = Math.max(rect.height - pinnedScene.getBoundingClientRect().height, 1);
      const ratio = Math.min(1, Math.max(0, -rect.top / scrollable));
      setOutcome(Math.min(panels.length - 1, Math.floor(ratio * panels.length)));
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    listen(state, window, 'scroll', requestUpdate, { passive: true });
    listen(state, window, 'resize', requestUpdate);
    state.cleanups.push(() => {
      if (frame) cancelAnimationFrame(frame);
    });
    update();
  }

  function initDisclosureRules(section, state) {
    const facts = [...section.querySelectorAll('.ec-facts__details details')];
    facts.forEach((item) => {
      listen(state, item, 'toggle', () => {
        if (!item.open) return;
        facts.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });

    const faqs = [...section.querySelectorAll('.ec-faq__accordion details')];
    faqs.forEach((item, index) => {
      listen(state, item, 'toggle', () => {
        if (!item.open) return;
        emit(section, 'faq_open', {
          faq_id: item.dataset.faqId || `faq_${index + 1}`,
          step_index: index
        });
      });
    });
  }

  function initOfferProgression(section, state) {
    section.querySelectorAll('a[href="#ec-primary-purchase"], [data-ec-offer-progression], [data-ec-offer-return]').forEach((control) => {
      listen(state, control, 'click', () => {
        emit(section, 'offer_progression', {
          source: control.dataset.ecOfferSource || control.className || 'purchase_anchor'
        });
      });
    });
  }

  function initSection(section) {
    if (states.has(section)) return;
    const state = { cleanups: [], observers: [] };
    states.set(section, state);
    activeRoots.add(section);
    section.dataset.ecR51Ready = 'true';

    initReveal(section, state);
    initSectionView(section, state);
    initDialogs(section, state);
    initProof(section, state);
    initRoutine(section, state);
    initSensory(section, state);
    initCounts(section, state);
    initOutcomes(section, state);
    initDisclosureRules(section, state);
    initOfferProgression(section, state);
  }

  function rootsWithin(root) {
    const roots = [];
    if (root?.matches?.(ROOT_SELECTOR)) roots.push(root);
    root?.querySelectorAll?.(ROOT_SELECTOR).forEach((section) => roots.push(section));
    return [...new Set(roots)];
  }

  function boot(root = document) {
    rootsWithin(root).forEach(initSection);
  }

  function destroy(section) {
    const state = states.get(section);
    if (!state) return;
    state.observers.forEach((observer) => observer.disconnect());
    state.cleanups.splice(0).reverse().forEach((cleanup) => {
      try { cleanup(); } catch (_error) { /* Section teardown is best effort. */ }
    });
    delete section.dataset.ecR51Ready;
    states.delete(section);
    activeRoots.delete(section);
  }

  function destroyWithin(root) {
    rootsWithin(root).forEach(destroy);
  }

  window[RUNTIME_KEY] = { boot, destroy, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
